import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BLOG_POSTS } from "../data/content";
import { Reveal } from "../components/Reveal";
import { PillButton } from "../components/PillButton";
import { ArrowLeft, ArrowUpRight } from "lucide-react";

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;

  const related = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <article data-testid="blog-post-page">
      <section className="pt-36 md:pt-44 pb-10 bg-white overflow-hidden">
        <div className="container-x max-w-4xl">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 font-heading font-bold text-[#0033A2] mb-8 hover:gap-3 transition-all"
            data-testid="blog-post-back"
          >
            <ArrowLeft size={18} strokeWidth={2.5} /> Back to blog
          </Link>
          <div className="flex items-center gap-3 text-sm font-semibold mb-6">
            <span className="bg-[#0033A2] text-white rounded-full px-3 py-1 text-xs uppercase tracking-wide">
              {post.category}
            </span>
            <span className="text-black/50">{post.date} · {post.read}</span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="font-heading font-black tracking-tighter text-4xl md:text-6xl leading-[0.95]"
          >
            {post.title}
          </motion.h1>
        </div>
      </section>

      <section className="pb-8">
        <div className="container-x max-w-4xl">
          <Reveal>
            <div className="rounded-3xl overflow-hidden border-2 border-black shadow-hard">
              <img
                src={`${post.cover}?auto=format&fit=crop&w=1400&q=80`}
                alt={post.title}
                className="w-full h-[280px] md:h-[460px] object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-20">
        <div className="container-x max-w-3xl">
          <div className="space-y-6">
            {post.body.map((para, i) => (
              <Reveal key={i} delay={0.02}>
                <p className="text-lg md:text-xl text-black/80 font-medium leading-relaxed">
                  {para}
                </p>
              </Reveal>
            ))}
          </div>

          <div className="mt-12 bg-brand-light border-2 border-black rounded-3xl p-8 md:p-10">
            <h3 className="font-heading font-extrabold text-2xl md:text-3xl tracking-tight">
              Ready to try grooh for your society?
            </h3>
            <p className="mt-3 text-black/65 font-medium">
              Book a free demo and see how much smoother community life can be.
            </p>
            <PillButton to="/contact" testid="blog-post-cta" className="mt-6">
              Book a free demo <ArrowUpRight size={18} strokeWidth={2.5} />
            </PillButton>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="pb-24 md:pb-32 bg-white">
        <div className="container-x">
          <h2 className="font-heading font-extrabold text-3xl md:text-4xl tracking-tight mb-10">
            Keep reading.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((p, i) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="group block"
                data-testid={`blog-related-${i}`}
              >
                <div className="h-full bg-brand-light rounded-3xl overflow-hidden border-2 border-transparent hover:border-black transition-all duration-300 hover:-translate-y-2 hover:shadow-hard-sm">
                  <div className="overflow-hidden h-44">
                    <img
                      src={`${p.cover}?auto=format&fit=crop&w=700&q=80`}
                      alt={p.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <span className="text-xs font-bold text-[#0033A2] uppercase tracking-wide">{p.category}</span>
                    <h3 className="mt-2 font-heading font-bold text-lg tracking-tight leading-snug group-hover:text-[#0033A2] transition-colors">
                      {p.title}
                    </h3>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </article>
  );
}
